/**
 * Price calculation utility to format the price
 * @class Price
 * @param {number} variantId - The variant id
 * @param {number} fromPrice - The minimum price
 * @param {number} fromCompareAtPrice - The minimum compare at price
 * @param {number} quantity - The quantity of the product
 */
class Price {
  constructor(variantId, fromPrice, fromCompareAtPrice, quantity) {
    this.variantId = variantId;
    this.fromPrice = fromPrice;
    this.fromCompareAtPrice = fromCompareAtPrice;
    this.quantity = quantity;
  }

  /**
   * Format price based on percentage decrease and generate token
   * @param {number} price
   * @param {number} compareAtPrice
   */
  format(price, compareAtPrice) {
    if (!compareAtPrice) {
      compareAtPrice = price;
    }

    // Set the min price
    if (price < this.fromPrice) {
      price = this.fromPrice;
    }

    // Set the min compare at price
    if (this.fromCompareAtPrice && compareAtPrice < this.fromCompareAtPrice) {
      compareAtPrice = this.fromCompareAtPrice;
    } else if (!this.fromCompareAtPrice && compareAtPrice < this.fromPrice) {
      compareAtPrice = this.fromPrice;
    }

    // Round up the price to 0.45 or 0.95 if price is not the same as the 'from' price
    if (price != this.fromPrice) {
      price = this.roundUpPrice(price);
    }
    if (compareAtPrice != this.fromCompareAtPrice && compareAtPrice != this.fromPrice) {
      compareAtPrice = this.roundUpPrice(compareAtPrice);
    }

    // Generate token form single unit price because function works with single price per unit
    const token = this.generateToken(price);

    // Set the price for the complete cart line based on the quantity
    const linePrice = price * this.quantity;
    const lineCompareAtPrice = compareAtPrice * this.quantity;

    return {
      price: price,
      line_price: linePrice,
      compare_at_price: compareAtPrice,
      line_compare_at_price: lineCompareAtPrice,
      token: token,
    };
  }

  /**
   * Round up price to 0.45 or 0.95
   * @param {number} price in cents
   * @returns {number} rounded up price in cents
   */
  roundUpPrice(price) {
    let cents = price % 100;
    if (cents < 5) {
      price = price - cents;
    } else if (cents < 20) {
      price = price - cents - 5;
    } else if (cents < 70) {
      price = price - cents + 45;
    } else {
      price = price - cents + 95;
    }
    return price;
  }

  /**
   * Generate the token based on the price and variant id
   * @info
   * This logic is here to camouflage the end price per unit since it's calculated in the frontend and send to the function in the backend with a line item property.
   * The ouput is a mix of the price an the variant id
   * Character 1 says how many characters are the euros (nth)
   * Character 5 and 6 are the cents (if cents are lower then 10, add a 0 in front)
   * Character 11 till (11 + nth) are the euros
   * The remaining characters are the variant id
   * @param {int} price
   * @returns {string} - token
   * @example getPriceFromToken('34709506153333146660') // 333.95
   * 3 470 95 0615 333 3146660 (without the spaces)
   * (Character 1 is 3, Character 5 and 6 are 95, Character 11 till (11 + 3) are 333)
   * @param {number} price
   * @returns {string} token
   */
  generateToken(price) {
    // Get the euros, cents and the length of the euros separately
    let euros = Math.floor(price / 100);
    let cents = price % 100;
    let euroLength = euros.toString().length;
    if (cents <= 9) {
      cents = `0${cents}`;
    }

    // Split the variant id in 3 parts
    const variantPart1 = this.variantId.toString().substring(0, 3);
    const variantPart2 = this.variantId.toString().substring(3, 7);
    const variantPart3 = this.variantId.toString().substring(7);

    // Euro length + variantPart1 + cents + variantPart2 + euros + variantPart3
    const token = `${euroLength}${variantPart1}${cents}${variantPart2}${euros}${variantPart3}`;

    return token;
  }
}

export default Price;
